import _ from 'lodash'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import globals from 'scoreboard/components/globals'
import enums from 'scoreboard/components/enums'
import calendars from 'scoreboard/components/calendars'
import StandardReportBaseModel from 'scoreboard/reports/common/standard-report-base-model'
import reportConstants from 'scoreboard/reports/report-constants'
import ReportStack from '../common/model/report-stack'
import ReportColumn from '../common/model/designer-column'
import Stacks from '../common/model/stacks'
import ChunkedArrayStream from '../common/chunked-array-stream'
import projects from 'scoreboard/projects/projects'
import constants from 'scoreboard/components/constants'
import unwrapModel from 'scoreboard/components/unwrap-model'
import arrayToHash from 'scoreboard/components/array-to-hash'
import arrayStream from 'stream-array'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, DATE_TIME } = DataType
import SummaryType from 'scoreboard/datasets/model/summary-type'
const { DAY_OF_THE_WEEK, MONTH_OF_THE_YEAR, SIMILAR_TIME_RANGES } = SummaryType

import { isColumnScored } from './scorecard-report-columns'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'


function defaultRange() {
	return {
		calendarId: null,
		first: -2,
		last: 0,
		relative: true
	}
}
function singleRange() {
	return _.defaults({ first: 0 }, defaultRange())
}

function defaultNoteDisplayOptions() {
	//see #34988
	return {
		displayType: reportConstants.NOTE_DISPLAY_TYPE_ALL,
		range: defaultRange(),
		displayProperties : {
			'createdDate' : true,
			'author': true,
			'replies': true,
			'appliesTo': true
		}
	}
}

const defaultColumnDataMap = _.memoize(function() {
	let columnDataByColumnType = _.cloneDeep(constants.scorecardReportColumnTypes)
	_.each(columnDataByColumnType, (columnData, columnType) => {
		if (columnType == reportConstants.REPORT_COLUMN_NOTES_COLUMN_TYPE_ID) {
			columnData.noteDisplayOptions = defaultNoteDisplayOptions()
		}
		delete columnData.id
		columnData.columnType = columnType
		columnData.style = {}
		columnData.aggregationType = columnData.defaultAggregationType
		columnData.abbreviated = true
		if (isColumnScored(columnType)) {
			columnData.removeScoringColor = false
			// #40225 Goal and Thresholds don't default to scored colors even though,
			// for other purposes, they may be referred to as "scored" columns.
			if (columnType != 'goal' && columnType != 'thresholds') {
				columnData.colorType = 'scoring'
			}
		}
	})
	return columnDataByColumnType
})

const CHANGE_PROPERTIES_TO_IGNORE = ['currentCalendarId','relativePeriodId','dirty']

/**
 * The data model representing the scorecard report
 */
const ScorecardReportModel = StandardReportBaseModel.extend({

	allowTemplate: false,
	designer: true,
	reportDomainType: 'scorecard-item',
	editing: false,
	fieldOrColumnTypeProperty: 'columnType',

	periodSelector() {
		return true
	},

	_defaultProperties() {
		return {
			projectId: projects.id,
			organizationId: globals.orgId,
			relativePeriodId: globals.periodId,
			currentCalendarId: calendars.periodsById[globals.periodId].calendar.id,
			type: 'report',
			label: i18n.get('report.common.new'),
			groupBy: [],
			showIndividualRecords: true
		}
	},

	defaults() {
		return _.extend({
			subtype: 'scorecard-report-designer',
			filters: {
				types: [{values: ['metric']}],
				projectIds: [{values:[projects.id]}]
			},
			structure: {
				stacks: [
					this._defaultStackData('label', {sortType: 'tree'}),
					this._defaultStackData('owners'),
					this._defaultStackData('actual')
				]
			}
		}, this._defaultProperties())
	},

	initialize: function(attrs, opts = {}) {
		StandardReportBaseModel.prototype.initialize.apply(this, arguments)

		//if this model generates an error in the server, include the xhr so we
		//can potentially debug/fix the report #46358
		if (opts.xhrError) {
			this._xhrErr = opts.xhrError
		}

		//specialize backbone collection that incorporates logic to work with designer stacks
		this._stacks = new Stacks(this.attributes.structure.stacks, {
			parse:true,
			reportDomainType: this.reportDomainType,
			reportModel: this
		})

		//on any change on the field or updates to the collection of fields
		//we may need to either refetch for data, or apply the changes to the
		//structure (to kick off a redraw)
		this._stacks.on('change update reset', (modelOrCollection, opts) => {
			this.set({'dirty': true})
			//if the models in the collection have changed we need update the
			//structure attribute on the designer model for two reasons:
			//1. to ensure we are sending the latest structure to the server if
			//we need to do a reload (fetch new data) as a result from the
			//changes in the models in the collection
			//2. to re-render the table as a result from the changes.
			if (this._stacks.refetch) {
				//if we're doing a reload, it will silently set the structure
				//so that we don't unnecessarily re-render the table before
				//the server has sent back the data.  Once the server sends back the
				//data, it will be parsed (via parseForm) and the models in the
				//collection will be updated with the latest server data and the
				//table will be re-rendered
				this.reload()
			} else {
				//if the changes do not required a reload, then table will be re-render
				//immediately from the result of the changes in the models in the stack
				//collection
				this.set('structure', { stacks: this._stacks.toJSON() })
			}
		})

		//debounce the fetch so as not to overload the server
		this._doFetch = _.debounce((fetchOptions) => {
			//#42767 delete the flag only when we actually do the fetch
			delete this._stacks.refetch

			if (this._latestFetch) {
				//#47759 on slow connections, the debounced fetch may fire multiple
				//times due to the slowness clearing the wait threshold.  In this
				//case we need to ignore the previous fetches as stale and only
				//resolve the against the last fetch.  So this marks any existing
				//fetches as stale, while the super call to fetch will reset
				//this_.latestFetch to the actual latest fetch
				this._latestFetch.stale = true
			}

			//ensure we send the latest structure to the server, but don't use
			//set method because we don't want backbone to prematurely dirty
			//the form if the structure hasn't changed')
			this.attributes.structure = {stacks: this._stacks.toJSON()}
			return StandardReportBaseModel.prototype.fetch.call(this, fetchOptions).then((model, def) => {
				if (!def.stale) {
					return this._currentFetch.resolve(model)
				}
			}, (model, xhr, def) => {
				if (!def.stale) {
					return this._currentFetch.reject(model, xhr)
				}
			})
		}, 500)

		//keep a ref to the current fetch to stay in line with Backbone API
		this._currentFetch = $.Deferred()

		this._setupListeners()
	},

	_parseStacks: function(stacks, opts = {}) {
		this._stacks.set(stacks, _.defaults({parse: true, silent: true}, opts, {merge: true}))
	},

	_setupListeners: function() {
		this.on('change:structure', (model, value, opts) => {
			if (opts.parse) {
				this._parseStacks(value.stacks, opts)
			}
		})

		this.on('change:relativePeriodId', this._onChangeCalendarPeriod.bind(this))

		//when switching off individual records, the grouped header that was being
		//shown will be forced to the first stack position to make it the left
		//most stack
		this.on('change:showIndividualRecords', () => {
			//#42767 set this first, to ensure we fetch due to change in aggregation
			this._stacks.refetch = true
			const groupingId = this.get('groupBy')[0]
			const groupedStackLookupResult = this.stacks()
				.findStackByColumnAttribute({ groupingId })

			if (!_.isEmpty(groupedStackLookupResult)) {
				// If grouping but not showing individual records, set bold to clarify
				const groupingColumn = groupedStackLookupResult.model.columns(0)
				const showIndividualRecords = this.get('showIndividualRecords')
				if (!showIndividualRecords) {
					groupingColumn.set('bold', true)
				}
			}
		})

		//if grouping or filters has changed we always need to reload
		this.on('change:groupBy change:filters', () => {
			this._stacks.refetch = true
		})

		this.on('change', (val, eventOpts = {}) => {
			let ignoringChange = _.isEmpty(_.omit(this.changedAttributes(), CHANGE_PROPERTIES_TO_IGNORE))
			//form doesn't become dirty if only the relative period changed
			//or the change is on the dirty property itself or
			//this change was fired from a update from the server
			if (eventOpts.serverUpdate || ignoringChange) {
				return
			}
			this.set({'dirty': true}, {silent: true})
			this.stale = true

			if (this._stacks.refetch) {
				this.reload()
			}
		})
	},

	_onChangeCalendarPeriod: function() {
		this.reload()
	},

	/**
	 * Set/Gets the attached stream
	 * @param {Stream|Array} stream the stream (or array) to use
	 */
	stream: function(stream, opts = {}) {
		if (arguments.length == 0) { return this._stream || new arrayStream([]) }
		_.invoke(this._stream, 'removeAllListeners')
		let cas
		if (stream instanceof Array) {
			cas = new ChunkedArrayStream(stream, opts)
			stream = cas.chunker()
		}
		this._stream = stream
		this._data = opts.allData || [] //internal structure holding the results
		let counter = this._data.length
		this._stream.on('data', data => {
			this._data = this._data.concat(data)
			//the initial update on a new stream replaces the rows, subsequent
			//calls append them
			let opts = {append: counter++ != 0}
			this.trigger('data', data, opts)
		})

		// notify interested of the new stream
		this.trigger('stream', stream)

		//start streaming only after interested parties are notified since
		//they may need to do additional setup before the data should be recieved
		_.invoke(cas, 'start')
	},

	/**
	 * Returns the data that has been emitted by the current attached stream
	 */
	data: function() {
		return this._data
	},

	/**
	 * Returns the error if any from the last request.
	 */
	error: function() {
		//first check to see if there was an xhr failure, if not then check
		//if the server send an error message
		return _.get(this._xhrErr, 'responseJSON',  _.get(this.metadata, 'error'))
	},

	validate(attrs = this.attributes, opts) {
		let errors = {}
		if (_.isEmpty(attrs.label)) {
			errors.label = i18n.get('common.error.required', i18n.get('report.title'))
		}
		let stackErrors = this._stacks.validate() || []
		if (!_.isEmpty(stackErrors)) {
			errors.meta = stackErrors
		}
		return this.formatErrors(errors, opts)
	},

	fetch(options = {}) {
		//sometimes all we need the model is for client side manipulation, in these
		//cases don't reach out to the server to see the results of any changes
		if (this.noFetch) {
			delete this._stacks.refetch
			return $.Deferred().resolve(this)
		}

		if (this.editing) {
			options = _.extend({}, reportConstants.EDIT_FETCH_OPTIONS, options)
		}

		//remove any previous errors triggered by the xhr
		delete this._xhrErr

		//Note: we must always parse the form on the fetch so that the client
		//is update to date with the latest data from the server otherwise you
		//get stuff like #44563
		let opts =  _.defaults({
			parseForm: (key, value) => {
				if (key == 'form') {
					this._parseForm(value)
				}
			}
		}, options, {
			error: (model, xhr) => {
				//if there was an error from the server store it so we can potentially
				//recover on the front #46358
				this._xhrErr = xhr
			}
		})

		if (this._currentFetch.state() != 'pending') {
			//previous fetch resolved, start a new one
			this._currentFetch = new $.Deferred()
		}
		this._doFetch(opts)
		return this._currentFetch
	},

	_parseForm: function(form) {
		//update the client with the latest stack structure from the server,
		//parse:true so the stack collection has the latest data
		//NOTE: silent must NOT be enabled for this to happen
		this.set('structure', form.structure, {parse: true, serverUpdate: true})

		// the server may remove specific color values from color filters, so
		// we need to update the client if any such removals occurred. Need to
		// take change the values and update the metadata
		const filters = this.get('filters')
		_.each(filters.colors, (c, i) => {
			const colors = form.filters.colors[i].values
			c.values = colors
			_.each(_.keys(c.metadata), k => {
				if (!_.includes(colors, k)) delete c.metadata[k]
			})
		})
		this.set('filters', filters)
	},

	/**
	 * Reloads the model from the server
	 */
	reload: function(args = {}) {
		//force enables to fetch regardless of dirty/persistence status
		return this.fetch(_.extend({force: true}, args))
	},

	/**
	 * Returns the designer stack specified at the index.  If no index
	 * is specified returns the Backbone Collection of designer stacks
	 * @param {Integer} [index] index of stack
	 */
	stacks(index = null) {
		return _.isNil(index) || isNaN(index) ? this._stacks : this._stacks.at(index)
	},

	/**
	 * Resets the designer stacks to the given stack/stacks
	 * @param {Array|Object} stacks the new designer stacks to use
	 * @param {Object} [opts] options passed along to Backbone.Collection.reset
	 */
	resetStacks(stacks, opts = {}) {
		this._stacks.reset(!_.isArray(stacks) ? [stacks] : stacks, opts)
	},

	/**
	 * Adds a stack at the given index
	 *
	 * @param {Integer} index the index to add
	 * @param {String|Integer|ReportColumn} fieldIdOrColumnTypeOrColumn The field
	 * id, column type, or Report Column of the new stack
	 * for datasets this is the field id, non datasets is the column type
	 */
	addStack(index, fieldIdOrColumnTypeOrColumn, opts = {}) {
		let { transient } = opts
		if (_.isNil(index)) {
			index = _.size(this._stacks)
		}
		let stackData
		if (fieldIdOrColumnTypeOrColumn instanceof ReportColumn) {
			stackData = {columns: [fieldIdOrColumnTypeOrColumn]}
		} else {
		 	stackData = this._defaultStackData(fieldIdOrColumnTypeOrColumn, {transient})
		}
		//#39635 pass along if we need show "New Column" instead of column label
		_.last(stackData.columns).newBlankColumn = !!opts.newBlankColumn

		let stack = new ReportStack(stackData, {parse: true, reportDomainType: this.reportDomainType})
		if (opts.replace) {
			this.removeStack(index, {silent: true})
		}
		this._stacks.add(stack, _.defaults({at: index}, opts))
		return stack
	},

	/**
	 * Adds a column to an existing multi layout stack at the given index within
	 * the multi layout
	 *
	 * @param {Integer} index the index within the breakdown add
	 * @param {ReportStack} stack The existing stack model containing a repeatBy stack
	 * @param {Integer|String|ReportColumn} fieldIdOrColumnType The dataset field id or column type to add,
	 * alternative this can be a ReportColumn object itself to add an existing
	 * column to an existing repeat by
	 * @param {Object} [opts.columnData=null] properties to override the default column
	 * data
	 */
	addColumnToRepeatBy(index, stack,
		fieldIdOrColumnType, opts = {}) {
		let { transient } = opts
		let columnModel
		if (fieldIdOrColumnType instanceof ReportColumn) {
			columnModel = fieldIdOrColumnType
		} else {
			let data = this._defaultColumnData(fieldIdOrColumnType, {transient})
			columnModel = new ReportColumn(data, {reportDomainType: this.reportDomainType})
		}
		columnModel.set('newBlankColumn', !!opts.newBlankColumn, {silent: true})

		let columnData = _.get(opts,'columnData', {})
		if (!_.isEmpty(columnData)) {
			columnModel.set(columnData, {silent: true})
		}

		if (stack) {
			let repeatBy = stack.get('repeatBy')
			let addingScoredField = isColumnScored(columnModel.get('columnType'))
			let timeRange = repeatBy.timeRange()
			if (addingScoredField && _.isNil(timeRange)) {
				//if the repeat by doesn't have a time range, and we're adding
				//a scored field, add a default range
				repeatBy.addBreakdown({ range: defaultRange(), transient: true })
			}

			let columns = stack.columns()
			if (opts.replace) {
				columns.remove(columns.at(index), {silent: true})

				//if we're replacing a field, check to see if we should remove
				//any transient time range breakdowns. (we should remove if
				//the repeat by no longer requires a time range and the time range
				//itself is transient)
				if (!addingScoredField && !stack.requiresTimeRange() && !_.isNil(timeRange)) {
					let transientTimeRange = _.filter(repeatBy.transientBreakdowns(), breakdown => {
						return !_.isNil(breakdown.get('range'))
					})
					repeatBy.removeBreakdown(transientTimeRange, {silent: true})
				}
			}
			columns.add(columnModel, _.defaults({at: index, parse: true}, opts))
		}
		return stack
	},

	/***
	 * Removes a stack at the given index.  Returns the stack removed or null
	 * if nothing was removed
	 *
	 * @param {Integer } the stack index
	 */
	removeStack(index, opts = {}) {
		return this._stacks.remove(this._stacks.at(index), opts)
	},

	reorderStack: function(oldIndex, newIndex, opts = {}) {
		let stackModel = this.removeStack(oldIndex, {silent: true})
		this._stacks.add(stackModel, _.extend({}, opts, {at: newIndex}))
	},

	_resolveStackArg(stackOrIndex) {
		return stackOrIndex instanceof ReportStack ? stackOrIndex :
			this._stacks.at(stackOrIndex)
	},

	/**
	 * Reorders the columns for a given repeat by stack
	 * @param {ReportStack|Integer} stackOrIndex The Repeat By Report Stack
	 * or the index of the repeat by stack that the reorder is targeting
	 * @param {Integer} oldColumnIndex The index of the column to move
	 * @param {Integer} newColumnIndex The new index where the column to move to
	 */
	reorderBreakdownStacks: function(stackOrIndex, oldColumnIndex, newColumnIndex) {
		let reportStack = this._resolveStackArg(stackOrIndex)
		let repeatByModel = reportStack && reportStack.get('repeatBy')
		if (!repeatByModel)  { return }

		let columns = reportStack.columns()
		let column = columns.at(oldColumnIndex)
		columns.remove(column, {silent: true})
		columns.add(column, {at: newColumnIndex})
	},

	/**
	 * Reorders the structure of the header order
	 *
	 * @param {ReportStack|Integer} stackOrIndex The Repeat By Report Stack
	 * or the index of the repeat by stack that the reorder is targeting
	 * @param {Integer} oldLevel The index of the level that is moving
	 * @param {Integer} newLevel The new index where that header row is moving to
	 */
	reorderRepeatByHeaderOrder: function(stackOrIndex, oldLevel, newLevel) {
		let reportStack = this._resolveStackArg(stackOrIndex)
		let repeatByModel = reportStack && reportStack.get('repeatBy')
		if (!repeatByModel)  { return }
		repeatByModel.reorderHeader(oldLevel, newLevel)
	},

	clone: function () {
		let newModel = StandardReportBaseModel.prototype.clone.apply(this, arguments)
		newModel._data = this.data()
		newModel.editing = this.editing
		newModel.metadata = _.cloneDeep(this.metadata)
		newModel._xhrErr = this._xhrErr  //copy over any request errors
		return newModel
	},

	/**
	 * Returns the grouping data.  These are the ids of the rows partitioned
	 * by their grouping attribute, if the current designer is grouping
	 */
	groupings: function() {
		return _.get(this.metadata, 'groupings', [])
	},

	/**
	 * Function that returns the column data (a field in datasets)
	 * by their column type id (the field id for datasets)
	 */
	columnDataByFieldIdOrColumnType: function(typeId) {
		let map = this._columnDataMap()
		if (typeId instanceof ReportColumn) {
			typeId = typeId.fieldIdOrColumnType()
		}
		return _.isNil(typeId) ? map : map[typeId]
	},

	/**
	 * Returns the default data object used to setup an initial breakdown.
	 * Subclass should override to accomodate type specific behavior
	 *
	 * @param fieldIdOrColumnType
	 */
	defaultBreakdownDataByFieldIdOrColumnType: function(fieldIdOrColumnType) {
		let data = {breakdownBy: fieldIdOrColumnType}
		let columnData = this.columnDataByFieldIdOrColumnType(fieldIdOrColumnType)
		if (columnData.changesOverTime) {
			//scored columns when breaking down do not set the breakdown by
			//because they aren't aggregated, instead the column's field is
			//used to determine the breakdown
			delete data.breakdownBy
			data.range = defaultRange()
		}
		return data
	},

	getDefaultRepeatByLabel: function(breakdown) {
		const columnType = unwrapModel(breakdown).breakdownBy
		return columnType &&
			i18n.get(this._defaultColumnData(columnType).label)
	},

	repeatByColumnOptionsList: function(stack, opts) {
		//handle the option list when adding as a "repeat by" column
		return _.filter(this.columnDataByFieldIdOrColumnType(), (columnData, fieldIdOrColumnType) => {
			if (columnData.canRepeatBy === false) {
				return false
			}
			if (columnData.changesOverTime && this.isAggregating()) {
				if (opts.forColumn) {
					//allow if we're adding/editing column, since this doesn't
					//affect the breakdown composition itself #42376
					return true
				}
				return false	//#42404 for scorecard reports, we don't want to do this
			}
			return true
		})
	},

	isAggregating: function() {
		return !this.isShowingIndividualRecords() && this.isGrouping()
	},

	nonRepeatByColumnOptionsList: function(stack) {
		let columnDataByTypeId = this.columnDataByFieldIdOrColumnType()
		let isRepeatByStack = stack && stack.has('repeatBy')
		let aggregating = this.isAggregating()

		//handle logic on what columns to show
		let columnsToShow = _.filter(columnDataByTypeId, (properties, columnType) => {
			let isScoredField = isColumnScored(columnType)
			if (isRepeatByStack && !aggregating) {
				//if repeat by but not aggregating then only show other scored fields
				return isScoredField
			}
			//otherwise we can add anything (adding a scored column will
			//implicitly create a multi layout column)
			return true
		})
		return columnsToShow
	},

	/**
	 * Returns an array of option data for the given stack
	 */
	columnOptionsList: function(stack, opts = {}) {
		let optionList
		if (opts.asRepeatBy || (stack && stack.isRepeatByStack())) {
			optionList = _.cloneDeep(this.repeatByColumnOptionsList(stack, opts))
			let columnTypesInStack = stack.fieldIdsOrColumnTypes({
				includeRepeatBy: true, asSet: true
			})

			//prevent repeat by grouped fields
			optionList = _.filter(optionList, option => {
				const alreadyGroupingBy =
					this.isGroupingByFieldIdOrColumnType(option.id)
				const alreadyInStack = columnTypesInStack.has(option.id)
				const currentlySelected = option.id == opts.currentFieldIdOrColType
				// #44773: If this is the currently selected option, (i.e. we're editing
				// a column), allow it in any case.
				return currentlySelected || (
					//allow if this field is not being grouped
					!alreadyGroupingBy &&
					//de-dup the fields in the repeat by
					!alreadyInStack
				)
			})
		} else {
			optionList = _.cloneDeep(this.nonRepeatByColumnOptionsList(stack, opts))

			//#41617 prevent changing group by column to a field/column in a repeat by
			if (stack && stack.hasGroupedColumn()) {
				let stacks = this.stacks().models
				let columnOrFieldsRepeatingBy = _(stacks)
					.filter(stack => stack.isRepeatByStack())
					.map(repeatByStack =>  {
						return repeatByStack.get('repeatBy').allBreakdownBys()
					})
					.flatten()
					.value()

				if (columnOrFieldsRepeatingBy && !_.isEmpty(columnOrFieldsRepeatingBy)) {
					columnOrFieldsRepeatingBy = arrayToHash(columnOrFieldsRepeatingBy)
					let approvedOptionList = []
					_.each(optionList, value => {
						if (!columnOrFieldsRepeatingBy[value.id]) {
							approvedOptionList.push(value)
						}
					})
					optionList = approvedOptionList
				}
			}
		}

		_.each(optionList, this._assignOptionGroup)

		return optionList
	},

	_assignOptionGroup: function(opt) {
		if (opt.requiresTimeRange) {
			opt.group = i18n.get('report.designer.repeatingColumns')
		} else {
			opt.group = 'headerless'
		}
	},

	getSummaryTypeOptionsList: function(columnOrBreakdown) {
		const dataTypeIncludesDate = _.includes([DATE, DATE_TIME],
			this.getDataType(columnOrBreakdown))
		if (dataTypeIncludesDate) {
			let optionIds = ['value', 'calendar']
			// #50424 need at least 2 calendars to group similar time ranges
			if (calendars.calendars.length >= 2) {
				optionIds.push('similar-time-ranges')
			}
			return this._buildSummaryTypeOptionsFromOptionIds(
				optionIds, columnOrBreakdown)
		} else {
			return this._buildNonDateColumnSummaryTypeOptions(columnOrBreakdown)
		}
	},

	_buildNonDateColumnSummaryTypeOptions: function(column) {
		return this._buildSummaryTypeOptionsFromOptionIds(
			['value'], column
		)
	},

	_buildSummaryTypeOptionsFromOptionIds: function(optionIds, columnOrBreakdown) {
		let summaryTypeOptions = []
		_.each(optionIds, id => {
			let msgKey = enums.summaryType[id]
			if (_.isNil(msgKey)) { return }
			if (id == 'calendar') {
				let calendarOptions = calendars.calendars.map(it => ({
					id: this.buildCalendarSummaryTypeOptionId('calendar', it.id),
					label: it.label
				}))
				summaryTypeOptions = summaryTypeOptions.concat(calendarOptions)
			} else {
				let label = i18n.get(msgKey)
				if (id == 'value' || id == 'range') {
					if (id == 'range') {
						msgKey = isNumericDataType(this.getDataType(columnOrBreakdown)) ?
							'datasets.choose.rows.range.numeric' :
							'datasets.choose.rows.range.text'
					}
					label = i18n.get(msgKey, this._getEveryXLabel(columnOrBreakdown))
				}
				summaryTypeOptions.push({id, label})
			}
		})
		return summaryTypeOptions
	},

	buildCalendarSummaryTypeOptionId: function(summaryType, groupCalendarId) {
		return `calendar-${groupCalendarId}`
	},

	getSummaryTypeOptionId: function(summaryType, groupCalendarId) {
		if (summaryType === DAY_OF_THE_WEEK) {
			return SIMILAR_TIME_RANGES
		}
		if (summaryType === MONTH_OF_THE_YEAR) {
			return SIMILAR_TIME_RANGES
		}
		if (summaryType === 'calendar') {
			return this.buildCalendarSummaryTypeOptionId(summaryType, groupCalendarId)
		}

		return summaryType
	},

	getDataType: function(columnOrBreakdown) {
		return columnOrBreakdown.get('dataType')
	},

	_getEveryXLabel: function(columnOrBreakdown) {
		if (columnOrBreakdown.has('range') || columnOrBreakdown.has('breakdownBy')) {
			if (columnOrBreakdown.has('range')) {
				return 'common.calendar.period'
			}
			let columnType = columnOrBreakdown.get('breakdownBy')
			return this._defaultColumnData(columnType).messageCode
		}
		return columnOrBreakdown.get('label')
	},

	/**
	 * Returns the default label for this field or column type
	 * @param {ReportColumn|String|Int} fieldIdOrColumnType
	 */
	defaultLabel: function(fieldIdOrColumnType) {
		if (fieldIdOrColumnType instanceof ReportColumn) {
			if (!!fieldIdOrColumnType.get('calculated')) {
				return ""
			}
			fieldIdOrColumnType = fieldIdOrColumnType.get(this.fieldOrColumnTypeProperty)
		}
		return this._defaultColumnData(fieldIdOrColumnType).label
	},

	cloneAsNew: function() {
		let newModel = StandardReportBaseModel.prototype.cloneAsNew.apply(this, arguments)
		let stacks = _.get(newModel.attributes, 'structure.stacks', [])

		let clearIds = (collection) => {
			collection && _.each(collection, item => {
				if (item) {
					delete item.id
				}
			})
		}

		if (!_.isEmpty(stacks)) {
			//clear out the ids of the stacks
			clearIds(stacks)

			//clear out the ids of the columns
			let columns = _.flatMap(stacks, stack => stack.columns)
			clearIds(columns)

			//clear out the ids related to dataset stuff
			let datasetAggregations = _.flatMap(columns, c => c.datasetAggregations)
			clearIds(datasetAggregations)
			let datasetFilters = _.flatMap(columns, c => c.datasetFilters)
			clearIds(datasetFilters)

			let repeatBys = _.map(stacks, stack => stack.repeatBy)
			clearIds(repeatBys)

			let breakdowns = _(stacks)
				.filter(stack => !_.isNil(stack.repeatBy))
				.flatMap(stack => stack.repeatBy.breakdowns)
				.value()
			clearIds(breakdowns)

			let advancedLayoutCells = _(stacks)
				.filter(stack => _.get(stack.advancedLayout, 'layoutMatrix', null) != null)
				.map(stack => stack.advancedLayout.layoutMatrix)
				.flattenDeep()
				.value()
			clearIds(advancedLayoutCells)
		}
		return newModel
	},

	isShowingIndividualRecords: function(attrs = this.attributes) {
		return _.get(attrs, 'showIndividualRecords', true)
	},

	isGrouping: function(attrs = this.attributes) {
		return !_.isEmpty(_.get(attrs, 'groupBy', []))
	},

	/**
	 * Returns a list of messages for any warnings that need to be display to
	 * the user
	 */
	warnings: function() {
		return this._stacks.warnings()
	},

	/**
	 * Given a column type id, returns the default object that represents the stack.
	 * Optionally takes an object that uses merges the defaults with the given
	 * value and returns the aggregated object as the default
	 */
	_defaultStackData: function(fieldIdOrColumnType, overrides = {}) {
		let stack = {columns: []}
		if (_.isArray(fieldIdOrColumnType)) {
			_.each(fieldIdOrColumnType, cti => {
				let s = this._defaultStackData(cti, overrides)
				stack.columns = stack.columns.concat(s.columns)
				stack.repeatBy = s.repeatBy
			})
		} else {
			stack.columns.push(this._defaultColumnData(fieldIdOrColumnType, overrides))
			if (isColumnScored(fieldIdOrColumnType)) {
				stack.repeatBy = {
					headerOrder: [reportConstants.HEADER_TYPE_BREAKDOWN_ROW, reportConstants.HEADER_TYPE_FIELD_ROW],
					breakdowns: [{ range: singleRange() }]
				}
			}
		}
		return stack
	},


	/**
	 * Generates the default column data for a given column type
	 */
	_defaultColumnData: function(columnType, overrides = {}) {
		let columnData = defaultColumnDataMap()[columnType]

		// #39282 avoid modifying overrides object as caller may use it again
		let data = _.defaults({}, overrides, columnData)

		return data
	},

	/**
	 * Resets the designer to the given attributes
	 */
	reset: function(attributes, opts = {}) {
		let structureChanged = !_.isEqual(this.attributes.structure, attributes.structure)

		this.unset('dirty')
		//set the structure separately so it can be re-parsed
		this.set(_.omit(attributes, 'structure'), {silent: true})

		if (structureChanged) {
			//because we're resetting, clear out the current stacks collection model
			//to ensure we don't carry over any dirty properties
			this._stacks.reset(null, {silent: true})
			//silent to prevent intermediate table rebuild, will be rebuilt on reload
			this.set('structure', attributes.structure, {silent: true})
			//parse the new stack structure
			this._parseStacks(attributes.structure.stacks)
		}
		this.reload()
	},

	_columnDataMap: function() {
		let dataMap = _.cloneDeep(constants.scorecardReportColumnTypes)
		return dataMap
	},

	/**
	 * Returns a map where the key is the fieldIdOrColumnType and value is the grouping id
	 * of the column being grouped
	 */
	groupingIdByFieldIdOrColumnType: function() {
		let _groupingIdByFieldOrColumnType = {}
		_.each(this.get('groupBy') || [], groupingId => {
			let stackWithGrouping = this.stacks().findStackByColumnAttribute({groupingId}).model
			if (stackWithGrouping) {
				let fieldIdOrColumnType = stackWithGrouping.findColumnByAttribute({groupingId}).fieldIdOrColumnType()
				_groupingIdByFieldOrColumnType[fieldIdOrColumnType] = groupingId
			}
		})
		return _groupingIdByFieldOrColumnType
	},

	isGroupingByFieldIdOrColumnType: function(fieldIdOrColumnType) {
		return !_.isNil(this.groupingIdByFieldIdOrColumnType()[fieldIdOrColumnType])
	},

	/**
	 * Shortcut to return a list of all Report Column not in the advanced layout
	 */
	basicLayoutColumns: function() {
		return _.flatMap(this.stacks().models, stack => stack.columns().models)
	},

	/**
	 * Shortcut to Enable/Disable Abbreviation for all existing numeric columns
	 */
	setAbbreviateValues: function(enable) {
		_.each(this.basicLayoutColumns(), column => {
			if (isNumericDataType(column.get('dataType'))) {
				column.set('abbreviated', enable)
			}
		})
	},

	/**
	 * Returns the index (0, 1) representing the first/second position if this
	 * column is being grouped otherwise -1
	 */
	groupByPosition: function(column) {
		if (_.isNil(column)) {
			return -1
		}
		let attrs = unwrapModel(column)
		let groupingId = attrs.groupingId || null
		let groupBy = this.get('groupBy') || []

		//determine the position of the grouping, if not yet grouping append
		//to end
		return _.findIndex(groupBy, item => item == groupingId)
	},

	/**
	 * Shortcut to return all the designer columns found in all stacks in order
	 */
	allColumns: function() {
		return _.flatMap(this.stacks().models, stack => stack.columns().models)
	},

	hasColumnFilters: function() {
		return false
	},

	hasEmptyDataset: function() {
		return false
	},

	/**
	 * Given a fieldId or columnType returns true/false if the column requires
	 * an aggregation type
	 */
	requiresAggregationType: function(fieldIdOrColumnType) {
		return this.isAggregating() &&
			!this.isGroupingByFieldIdOrColumnType(fieldIdOrColumnType) &&
			fieldIdOrColumnType !== 'number-of-records'
	}

})

_.extend(ScorecardReportModel, {
	defaultNoteDisplayOptions,
	defaultRange
})

export default ScorecardReportModel
export { defaultNoteDisplayOptions }
export { defaultRange }
ScorecardReportModel.__filename__ = 'reports/scorecard-report/scorecard-report-model.js'
